import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { formatErrorMessage } from './utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_SA_FORM = {
  sales_org_id: '', dist_channel_id: '', division_id: '', sales_district_id: '',
  customer_group_id: '', sales_office_id: '', sales_group_id: '', currency_code: '',
  customer_pricing_procedure: '', delivery_priority_id: '', shipping_conditions_id: '',
  delivering_plant_id: '', incoterms_id: '', incoterms_location: '', terms_of_payment_sd: '',
  account_assignment_group: '', credit_control_area: '', order_combination: false,
  pod_relevant: false, rebate: false, max_partial_deliveries: null,
};

const EMPTY_CC_FORM = {
  company_code_id: '', reconciliation_acct: '', terms_of_payment_fi: '', record_pay_history: false,
};

/**
 * Container hook for the Customer master form. Owns all master-data loading,
 * form state and CRUD handlers, and returns a `ctx` bag consumed by the
 * presentational tab components plus the values the form shell needs.
 */
export const useCustomerForm = (mode = 'create', customerId = null) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const readOnly = mode === 'view';

  // Dropdown options
  const [accountGroups, setAccountGroups] = useState([]);
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [termsOfPayment, setTermsOfPayment] = useState([]);

  // SD dropdown options
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [distChannels, setDistChannels] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [salesDistricts, setSalesDistricts] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [salesOffices, setSalesOffices] = useState([]);
  const [salesGroups, setSalesGroups] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [deliveryPriorities, setDeliveryPriorities] = useState([]);
  const [shippingConditions, setShippingConditions] = useState([]);
  const [plants, setPlants] = useState([]);
  const [incoterms, setIncoterms] = useState([]);
  const [creditControlAreas, setCreditControlAreas] = useState([]);
  const [salesAreas, setSalesAreas] = useState([]);

  // Assignment-filtered dropdowns
  const [filteredDistChannels, setFilteredDistChannels] = useState([]);
  const [filteredDivisions, setFilteredDivisions] = useState([]);
  const [filteredSalesGroups, setFilteredSalesGroups] = useState([]);

  // Number range info
  const [numberRangeInfo, setNumberRangeInfo] = useState(null);
  const [loadingNumberRange, setLoadingNumberRange] = useState(false);
  const [customerIdError, setCustomerIdError] = useState('');

  // Form data — general data only
  const [formData, setFormData] = useState({
    account_group_id: '', customer_id: '', title: 'Company', name: '', search_term: '',
    street_house_no: '', postal_code: '', city: '', country: '', region: '',
    language: 'EN', mobile_number: '', email: '', cust_classification_id: '',
  });

  // Company code assignments
  const [companyCodeData, setCompanyCodeData] = useState([]);
  const [ccFormData, setCcFormData] = useState({ ...EMPTY_CC_FORM });
  const [showCcDialog, setShowCcDialog] = useState(false);
  const [editingCcIndex, setEditingCcIndex] = useState(null);

  // Sales area assignments
  const [salesAreaData, setSalesAreaData] = useState([]);
  const [saFormData, setSaFormData] = useState({ ...EMPTY_SA_FORM });
  const [showSaDialog, setShowSaDialog] = useState(false);
  const [editingSaIndex, setEditingSaIndex] = useState(null);

  // Partner function assignments
  const [availablePartnerFunctions, setAvailablePartnerFunctions] = useState([]);
  const [partnerFunctionAssignments, setPartnerFunctionAssignments] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);

  useEffect(() => {
    const initializeData = async () => {
      await loadInitialData();
      if ((mode === 'view' || mode === 'edit') && customerId) {
        await loadCustomerData();
      }
    };
    initializeData();
  // eslint-disable-next-line
  }, [mode, customerId]);

  const loadInitialData = async () => {
    try {
      const agResponse = await axios.get(`${BACKEND_URL}/api/customer-account-groups`);
      setAccountGroups(agResponse.data);
      const countryResponse = await axios.get(`${BACKEND_URL}/api/master-data/countries`);
      setCountries(countryResponse.data);
      const langResponse = await axios.get(`${BACKEND_URL}/api/master-data/languages`);
      setLanguages(langResponse.data);
      const ccResponse = await axios.get(`${BACKEND_URL}/api/company/company-codes`);
      setCompanyCodes(ccResponse.data);
      const topResponse = await axios.get(`${BACKEND_URL}/api/customer-terms-of-payments`);
      setTermsOfPayment(topResponse.data);

      const [soRes, dcRes, divRes, sdRes, cgRes, sofRes, sgRes, curRes, dpRes, scRes, plRes, incRes, ccaRes, saRes] =
        await Promise.all([
          axios.get(`${BACKEND_URL}/api/sales-distribution/sales-organizations`),
          axios.get(`${BACKEND_URL}/api/sales-distribution/distribution-channels`),
          axios.get(`${BACKEND_URL}/api/sales-distribution/divisions`),
          axios.get(`${BACKEND_URL}/api/customer-sales-districts`),
          axios.get(`${BACKEND_URL}/api/customer-groups`),
          axios.get(`${BACKEND_URL}/api/sales-distribution/sales-offices`),
          axios.get(`${BACKEND_URL}/api/sales-distribution/sales-groups`),
          axios.get(`${BACKEND_URL}/api/master-data/currencies`),
          axios.get(`${BACKEND_URL}/api/customer-delivery-priorities`),
          axios.get(`${BACKEND_URL}/api/customer-shipping-conditions`),
          axios.get(`${BACKEND_URL}/api/material-management/plants`),
          axios.get(`${BACKEND_URL}/api/customer-incoterms`),
          axios.get(`${BACKEND_URL}/api/finance/credit-control-areas`),
          axios.get(`${BACKEND_URL}/api/sales-distribution/sales-areas`),
        ]);

      setSalesOrgs(soRes.data);
      setDistChannels(dcRes.data);
      setDivisions(divRes.data);
      setSalesDistricts(sdRes.data);
      setCustomerGroups(cgRes.data);
      setSalesOffices(sofRes.data);
      setSalesGroups(sgRes.data);
      setCurrencies(curRes.data);
      setDeliveryPriorities(dpRes.data);
      setShippingConditions(scRes.data);
      setPlants(plRes.data);
      setIncoterms(incRes.data);
      setCreditControlAreas(ccaRes.data);
      setSalesAreas(saRes.data);
      setFilteredDistChannels(dcRes.data);
      setFilteredDivisions(divRes.data);
      setFilteredSalesGroups(sgRes.data);

      try {
        const customersResponse = await axios.get(`${BACKEND_URL}/api/customers`);
        setAllCustomers(customersResponse.data);
      } catch (error) {
        console.error('Error loading customers for partner functions:', error);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load dropdown options');
    }
  };

  const loadCustomerData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/customers/${customerId}`);
      const customer = response.data;

      setFormData({
        account_group_id: customer.account_group_id,
        customer_id: customer.customer_id,
        title: customer.title,
        name: customer.name,
        search_term: customer.search_term || '',
        street_house_no: customer.street_house_no,
        postal_code: customer.postal_code,
        city: customer.city,
        country: customer.country,
        region: customer.region,
        language: customer.language,
        mobile_number: customer.mobile_number || '',
        email: customer.email || '',
        cust_classification_id: customer.cust_classification_id || '',
      });
      setCompanyCodeData(customer.company_code_data || []);
      setSalesAreaData(customer.sales_area_data || []);
      setPartnerFunctionAssignments(customer.partner_function_data || []);

      if (customer.account_group_id) {
        try {
          let pfResponse = await axios.get(
            `${BACKEND_URL}/api/customer-account-group-partner-functions/account-group/${customer.account_group_id}`
          );
          if (!pfResponse.data || pfResponse.data.length === 0) {
            try {
              const pdpResponse = await axios.get(
                `${BACKEND_URL}/api/customer-account-group-pdps/account-group/${customer.account_group_id}`
              );
              if (pdpResponse.data && pdpResponse.data.procedure_id) {
                const pdpPfResponse = await axios.get(
                  `${BACKEND_URL}/api/customer-pdp-partner-functions/procedure/${pdpResponse.data.procedure_id}`
                );
                if (pdpPfResponse.data && pdpPfResponse.data.length > 0) {
                  pfResponse = { data: pdpPfResponse.data };
                }
              }
            } catch (pdpError) {
              console.error('Error checking Partner Determination Procedure:', pdpError);
            }
          }
          if (pfResponse.data && pfResponse.data.length > 0) {
            const pfDetailsPromises = pfResponse.data.map(async (assignment) => {
              const detailResponse = await axios.get(
                `${BACKEND_URL}/api/customer-partner-functions/${assignment.partner_function_id}`
              );
              return detailResponse.data;
            });
            const partnerFunctions = await Promise.all(pfDetailsPromises);
            setAvailablePartnerFunctions(partnerFunctions);
          }
        } catch (error) {
          console.error('Error loading partner functions:', error);
        }
      }
      toast.success('Customer loaded successfully');
    } catch (error) {
      console.error('Error loading customer:', error);
      toast.error(error.response?.data?.detail || 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountGroupChange = async (accountGroupId) => {
    setFormData({ ...formData, account_group_id: accountGroupId, customer_id: '' });
    setNumberRangeInfo(null);
    if (!accountGroupId) {
      setAvailablePartnerFunctions([]);
      return;
    }

    try {
      setLoadingNumberRange(true);
      const response = await axios.get(`${BACKEND_URL}/api/customers/number-range/${accountGroupId}`);
      setNumberRangeInfo(response.data);
      if (!response.data.external) {
        setFormData((prev) => ({ ...prev, customer_id: '' }));
        toast.success('Customer ID will be auto-generated');
      } else {
        toast.success('Enter Customer ID manually (External Numbering)');
      }
    } catch (error) {
      console.error('Error loading number range:', error);
      toast.error(error.response?.data?.detail || 'Failed to load number range');
    } finally {
      setLoadingNumberRange(false);
    }

    try {
      let pfResponse = await axios.get(
        `${BACKEND_URL}/api/customer-account-group-partner-functions/account-group/${accountGroupId}`
      );
      if (!pfResponse.data || pfResponse.data.length === 0) {
        try {
          const pdpResponse = await axios.get(
            `${BACKEND_URL}/api/customer-account-group-pdps/account-group/${accountGroupId}`
          );
          if (pdpResponse.data && pdpResponse.data.procedure_id) {
            const pdpPfResponse = await axios.get(
              `${BACKEND_URL}/api/customer-pdp-partner-functions/procedure/${pdpResponse.data.procedure_id}`
            );
            if (pdpPfResponse.data && pdpPfResponse.data.length > 0) {
              pfResponse = { data: pdpPfResponse.data };
              toast(`Partner functions loaded from Partner Determination Procedure: ${pdpResponse.data.procedure_id}`, {
                icon: 'ℹ️', duration: 4000,
              });
            } else {
              setAvailablePartnerFunctions([]);
              setPartnerFunctionAssignments([]);
              toast.error('No partner functions assigned to the Partner Determination Procedure for this Account Group');
              return;
            }
          } else {
            setAvailablePartnerFunctions([]);
            setPartnerFunctionAssignments([]);
            toast.error('No partner functions configured for this Account Group. Please assign partner functions directly or via Partner Determination Procedure.');
            return;
          }
        } catch (pdpError) {
          console.error('Error checking Partner Determination Procedure:', pdpError);
          setAvailablePartnerFunctions([]);
          setPartnerFunctionAssignments([]);
          toast.error('No partner functions configured for this Account Group. Please configure partner functions or assign a Partner Determination Procedure.');
          return;
        }
      } else {
        toast.success('Partner functions loaded from direct assignment');
      }

      const pfDetailsPromises = pfResponse.data.map(async (assignment) => {
        const detailResponse = await axios.get(
          `${BACKEND_URL}/api/customer-partner-functions/${assignment.partner_function_id}`
        );
        return detailResponse.data;
      });
      const partnerFunctions = await Promise.all(pfDetailsPromises);
      setAvailablePartnerFunctions(partnerFunctions);
      setPartnerFunctionAssignments([]);
    } catch (error) {
      console.error('Error loading partner functions:', error);
      setAvailablePartnerFunctions([]);
      setPartnerFunctionAssignments([]);
      toast.error('Failed to load partner function configuration');
    }

    try {
      const customersResponse = await axios.get(`${BACKEND_URL}/api/customers`);
      setAllCustomers(customersResponse.data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const handleInputChange = (field, value) => setFormData({ ...formData, [field]: value });

  const validateCustomerIdRealTime = (custId) => {
    if (!custId || !custId.trim()) { setCustomerIdError(''); return; }
    if (numberRangeInfo && numberRangeInfo.prefix) {
      const prefix = numberRangeInfo.prefix.toUpperCase();
      const upperCustomerId = custId.toUpperCase();
      if (!upperCustomerId.startsWith(prefix)) {
        setCustomerIdError(`Customer ID must start with prefix '${prefix}'`);
        toast.error(`Customer ID must start with prefix '${prefix}'`, { duration: 3000 });
      } else {
        const afterPrefix = upperCustomerId.substring(prefix.length);
        if (afterPrefix.length !== 8) {
          setCustomerIdError(`Customer ID must be ${prefix} followed by exactly 8 digits`);
        } else if (!/^\d+$/.test(afterPrefix)) {
          setCustomerIdError(`After prefix '${prefix}', remaining characters must be 8 numeric digits`);
        } else {
          setCustomerIdError('');
        }
      }
    } else {
      setCustomerIdError('');
    }
  };

  const handleSalesOrgChange = async (salesOrgId) => {
    setSaFormData((prev) => ({ ...prev, sales_org_id: salesOrgId, dist_channel_id: '', division_id: '' }));
    if (!salesOrgId) {
      setFilteredDistChannels(distChannels);
      setFilteredDivisions(divisions);
      return;
    }
    const validSalesAreasForSO = salesAreas.filter((sa) => sa.sales_org_id === salesOrgId);
    const validDCIds = [...new Set(validSalesAreasForSO.map((sa) => sa.dist_channel_id))];
    const filteredDC = distChannels.filter((dc) => validDCIds.includes(dc.dist_channel_id));
    setFilteredDistChannels(filteredDC);
    const validDivIds = [...new Set(validSalesAreasForSO.map((sa) => sa.division_id))];
    const filteredDiv = divisions.filter((div) => validDivIds.includes(div.division_id));
    setFilteredDivisions(filteredDiv);
    if (filteredDC.length === 0 || filteredDiv.length === 0) {
      toast.error(`No valid Sales Areas configured for Sales Organization '${salesOrgId}'`);
    }
  };

  const handleSalesOfficeChange = async (salesOfficeId) => {
    setSaFormData({ ...saFormData, sales_office_id: salesOfficeId, sales_group_id: '' });
    if (!salesOfficeId) { setFilteredSalesGroups(salesGroups); return; }
    try {
      const sgResponse = await axios.get(
        `${BACKEND_URL}/api/assignments/sales-office-group/by-sales-office/${salesOfficeId}`
      );
      const assignedSGIds = sgResponse.data.map((a) => a.sales_group_id);
      setFilteredSalesGroups(salesGroups.filter((sg) => assignedSGIds.includes(sg.sales_group_id)));
    } catch (error) {
      console.error('Error loading sales group assignments:', error);
      toast.error('Failed to load valid Sales Groups');
    }
  };

  const handleAddCompanyCode = () => {
    if (!ccFormData.company_code_id || !ccFormData.reconciliation_acct || !ccFormData.terms_of_payment_fi) {
      toast.error('Please fill all required Company Code fields');
      return;
    }
    const duplicate = companyCodeData.find((cc) => cc.company_code_id === ccFormData.company_code_id);
    if (duplicate && editingCcIndex === null) {
      toast.error(`Company Code '${ccFormData.company_code_id}' already added`);
      return;
    }
    if (editingCcIndex !== null) {
      const updated = [...companyCodeData];
      updated[editingCcIndex] = { ...ccFormData };
      setCompanyCodeData(updated);
      toast.success('Company Code updated');
    } else {
      setCompanyCodeData([...companyCodeData, { ...ccFormData }]);
      toast.success('Company Code added');
    }
    setCcFormData({ ...EMPTY_CC_FORM });
    setShowCcDialog(false);
    setEditingCcIndex(null);
  };

  const handleEditCompanyCode = (index) => {
    setCcFormData(companyCodeData[index]);
    setEditingCcIndex(index);
    setShowCcDialog(true);
  };

  const handleDeleteCompanyCode = (index) => {
    setCompanyCodeData(companyCodeData.filter((_, i) => i !== index));
    toast.success('Company Code removed');
  };

  const handleAddSalesArea = () => {
    if (!saFormData.sales_org_id || !saFormData.dist_channel_id || !saFormData.division_id) {
      toast.error('Sales Organization, Distribution Channel, and Division are required');
      return;
    }
    const salesAreaExists = salesAreas.some(
      (sa) => sa.sales_org_id === saFormData.sales_org_id &&
        sa.dist_channel_id === saFormData.dist_channel_id &&
        sa.division_id === saFormData.division_id
    );
    if (!salesAreaExists) {
      toast.error(`Invalid Sales Area: ${saFormData.sales_org_id} + ${saFormData.dist_channel_id} + ${saFormData.division_id}`);
      return;
    }
    const duplicate = salesAreaData.find(
      (sa) => sa.sales_org_id === saFormData.sales_org_id &&
        sa.dist_channel_id === saFormData.dist_channel_id &&
        sa.division_id === saFormData.division_id
    );
    if (duplicate && editingSaIndex === null) {
      toast.error('This Sales Area is already added');
      return;
    }
    const dataToSave = {
      ...saFormData,
      max_partial_deliveries:
        saFormData.max_partial_deliveries === '' || saFormData.max_partial_deliveries === null
          ? null
          : parseInt(saFormData.max_partial_deliveries),
    };
    if (editingSaIndex !== null) {
      const updated = [...salesAreaData];
      updated[editingSaIndex] = dataToSave;
      setSalesAreaData(updated);
      toast.success('Sales Area updated');
    } else {
      setSalesAreaData([...salesAreaData, dataToSave]);
      toast.success('Sales Area added');
    }
    setSaFormData({ ...EMPTY_SA_FORM });
    setShowSaDialog(false);
    setEditingSaIndex(null);
    setFilteredDistChannels(distChannels);
    setFilteredDivisions(divisions);
  };

  const handleEditSalesArea = (index) => {
    setSaFormData(salesAreaData[index]);
    setEditingSaIndex(index);
    setShowSaDialog(true);
    handleSalesOrgChange(salesAreaData[index].sales_org_id);
  };

  const handleDeleteSalesArea = (index) => {
    setSalesAreaData(salesAreaData.filter((_, i) => i !== index));
    toast.success('Sales Area removed');
  };

  const handleDeletePartnerFunction = (index) => {
    setPartnerFunctionAssignments(partnerFunctionAssignments.filter((_, i) => i !== index));
    toast.success('Partner Function removed');
  };

  const validateGeneralData = () => {
    const required = ['account_group_id', 'title', 'name', 'street_house_no', 'postal_code', 'city', 'country', 'region', 'language'];
    for (const field of required) {
      if (!formData[field] || !formData[field].toString().trim()) {
        toast.error(`${field.replace(/_/g, ' ')} is required`);
        return false;
      }
    }
    if (numberRangeInfo && numberRangeInfo.external) {
      if (!formData.customer_id || !formData.customer_id.trim()) {
        toast.error('Customer ID is required for external numbering');
        return false;
      }
      if (numberRangeInfo.prefix) {
        const prefix = numberRangeInfo.prefix.toUpperCase();
        const upperCustomerId = formData.customer_id.toUpperCase();
        if (!upperCustomerId.startsWith(prefix)) {
          toast.error(`Customer ID must start with prefix '${prefix}'`);
          return false;
        }
        const afterPrefix = upperCustomerId.substring(prefix.length);
        if (afterPrefix.length !== 8) {
          toast.error(`Customer ID must be ${prefix} followed by exactly 8 digits`);
          return false;
        }
        if (!/^\d+$/.test(afterPrefix)) {
          toast.error(`After prefix '${prefix}', remaining characters must be 8 numeric digits`);
          return false;
        }
      }
    }
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error('Invalid email format');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    if (!validateGeneralData()) return;
    setLoading(true);
    try {
      const partner_function_data = availablePartnerFunctions.map((pf) => {
        const assignment = partnerFunctionAssignments.find(
          (a) => a.partner_function_id === pf.partner_function_id && a.partner_number
        );
        return {
          partner_function_id: pf.partner_function_id,
          partner_number: assignment?.partner_number || '',
        };
      });
      const validCompanyCodeData = companyCodeData.filter(
        (cc) => cc.company_code_id && cc.reconciliation_acct && cc.terms_of_payment_fi
      );
      const validSalesAreaData = salesAreaData.filter(
        (sa) => sa.sales_org_id && sa.dist_channel_id && sa.division_id
      );
      const customerData = {
        ...formData,
        company_code_data: validCompanyCodeData,
        sales_area_data: validSalesAreaData,
        partner_function_data,
      };
      if (mode === 'create') {
        const response = await axios.post(`${BACKEND_URL}/api/customers`, customerData);
        toast.success(`Customer created successfully: ${response.data.customer_id}`);
        setTimeout(() => navigate('/customer-master'), 1500);
      } else if (mode === 'edit') {
        const response = await axios.put(`${BACKEND_URL}/api/customers/${customerId}`, customerData);
        toast.success(`Customer updated successfully: ${response.data.customer_id}`);
        setTimeout(() => navigate(`/customer-view/${customerId}`), 1500);
      }
    } catch (error) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} customer:`, error);
      const errorMessage = error.response?.data?.detail
        ? formatErrorMessage(error.response.data.detail)
        : `Failed to ${mode === 'create' ? 'create' : 'update'} customer`;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getPageHeader = () => {
    switch (mode) {
      case 'view':
        return { title: 'View Customer', subtitle: 'View customer master data across different views' };
      case 'edit':
        return { title: 'Edit Customer', subtitle: 'Update customer master data across different views' };
      default:
        return { title: 'Create Customer', subtitle: 'Enter customer master data across different views' };
    }
  };

  const ctx = {
    loading, setActiveTab, navigate,
    formData, handleInputChange, handleAccountGroupChange, validateCustomerIdRealTime,
    customerIdError, numberRangeInfo, loadingNumberRange, accountGroups, countries, languages,
    mode, readOnly, customerId,
    companyCodeData, ccFormData, setCcFormData, showCcDialog, setShowCcDialog,
    editingCcIndex, setEditingCcIndex, companyCodes, termsOfPayment,
    handleAddCompanyCode, handleEditCompanyCode, handleDeleteCompanyCode,
    salesAreaData, saFormData, setSaFormData, showSaDialog, setShowSaDialog,
    editingSaIndex, setEditingSaIndex, salesOrgs, distChannels,
    filteredDistChannels, setFilteredDistChannels, divisions, filteredDivisions, setFilteredDivisions,
    salesDistricts, customerGroups, salesOffices, salesGroups,
    filteredSalesGroups, setFilteredSalesGroups, currencies, deliveryPriorities,
    shippingConditions, plants, incoterms, creditControlAreas, salesAreas,
    handleSalesOrgChange, handleSalesOfficeChange,
    handleAddSalesArea, handleEditSalesArea, handleDeleteSalesArea,
    availablePartnerFunctions, partnerFunctionAssignments, setPartnerFunctionAssignments,
    allCustomers, handleDeletePartnerFunction,
  };

  return { ctx, handleSubmit, pageHeader: getPageHeader(), activeTab, setActiveTab };
};
